/**
 * Desktop-only view-model selectors (INVENTORY-DESKTOP §7.5).
 *
 * The desktop build is a second VIEW TREE, not a second app: `selectors.ts`,
 * `selectors.3b/3c/3d/3e.ts` and every slice apply unchanged (§7.4). This file
 * holds the short list of things the desktop template derives that mobile never
 * did — five additions and two copy constants, nothing more.
 *
 * WHY A SEPARATE FILE. `selectors.ts` is frozen behaviour with a golden fixture
 * (`data/fixtures/selectors.fixture.json`) and five parallel screen lanes
 * reading it. The two "additive field" changes §7.5 asks for (`VoteChip.title`,
 * `tfChips[].title`) are therefore applied here as WRAPPERS — `tonightVMDesktop`
 * re-maps `tonightVM`'s output instead of editing it — so the mobile tree, its
 * tests and the fixture are untouched. Same reasoning for `filterBarVM`, which
 * renames `filterButton`'s `fltBtn*` fields rather than exporting the private
 * helper out of `selectors.ts`.
 *
 * Everything here is pure, takes an explicit `now` where time matters, and mints
 * no closures (PORTING-NOTES P2-01).
 */

import { chipOf } from '../lib/format.ts';
import { fltCount, isCall, LVL, SORT_OPTS, TIERS } from '../lib/logic.ts';
import type { GameId, SortMode, Tier } from '../lib/types.ts';
import { bTagsOf, fltOf, xTagsOf } from './filterKey.ts';
import { filtersVM, navCurOf, tonightVM } from './selectors.ts';
import type { CrewOwnershipRow } from '../repo/Repository.ts';
import type { QdStore } from './types.ts';

// ---------------------------------------------------------------------------
// 1. top nav (§2.2)
// ---------------------------------------------------------------------------

export interface DesktopNavItem {
  key: string;
  /** the desktop label — mobile's tabs read MINE / GOTY and had no FRIENDS */
  l: string;
  clr: string;
  bd: string;
}

/**
 * The five nav labels (DESIGN-CHANGELOG 2026-09-03 "Nav label"). ME leads —
 * the page about you, and where every sign-in lands. TONIGHT? is an ordinary
 * mono nav item now: the pixel-font `GAMES TONIGHT?` lockup left the header
 * and the coin is the home mark. MY INTERESTS and GOTY are doors inside ME
 * (their pages light the ME item, via `navCurOf`), which is what freed the two
 * slots. Neither `profile` (the account chip) nor `detail` (any tile) appears.
 */
const NAV: ReadonlyArray<{ l: string; s: QdStore['screen'] }> = [
  { l: 'ME', s: 'mine' },
  { l: 'TONIGHT?', s: 'tonight' },
  // SCHEDULE, between TONIGHT? and DISCOVER (DESIGN-CHANGELOG 2026-10-14). A
  // night's page lights it too, via `navCurOf`.
  { l: 'SCHEDULE', s: 'schedule' },
  { l: 'DISCOVER', s: 'discover' },
  { l: 'PLAYTESTS', s: 'playtests' },
  // PARTY, not FRIENDS (2026-08-28): the screen is the roster of the party you
  // are in, and "friends" was never what it listed. The screen key stays `crew`.
  { l: 'PARTY', s: 'crew' },
];

/**
 * The top-bar view model. `headerVM` already supplies `cur`, every `isX` gate,
 * the What's-New pill state (`wnBd` / `wnClr` / `hasNew` / `newCount`) and the
 * avatar chip fields (`meIni` / `meHueBg` / `meHasAv` / `meAvBg`) — this adds
 * only what is genuinely desktop.
 *
 * The prototype's per-item count badge (`n.hasCount` / `n.count`, template line
 * 89) is a DEAD BRANCH: its source array sets neither key, so no item can ever
 * show a count (§9.4). Not reproduced.
 */
export function desktopNavVM(s: QdStore) {
  const me = s.me();
  const dg = s.screen === 'detail' ? s.gById(s.detailId) : null;
  const scr = s.screen === 'detail' && !dg ? 'discover' : s.screen;
  const cur = navCurOf(scr === 'detail' ? s.from : scr);
  return {
    navItems: NAV.map<DesktopNavItem>((n) => ({
      key: n.s,
      l: n.l,
      clr: cur === n.s ? 'var(--acc)' : 'var(--gt-text-5)',
      bd: cur === n.s ? 'var(--acc)' : 'transparent',
    })),
    /**
     * The nav chip is your NAME now, and only your name (Marcus, 2026-08-28:
     * who leads the party belongs in the PARTY view, not stamped across the
     * app). It used to read `MARCUS · ADMIN` — a rank in permanent chrome,
     * which is exactly the "everywhere" the rename was told to avoid. The
     * roster and the party switcher still say LEADER, because there the role
     * is the information rather than decoration; `meRoleTxt` is gone rather
     * than blanked, so nothing renders a stray separator.
     */
    meName: (me ? me.name : '').toUpperCase(),
  };
}

// ---------------------------------------------------------------------------
// 2. filter bar (§6.1)
// ---------------------------------------------------------------------------

/**
 * `filtersVM` plus the trigger-button state.
 *
 * The button fields exist today as `fltBtnTxt/Bd/Clr/Bg` on each SCREEN's VM
 * (`discoverVM`, `tonightVM`, `interestsVM`, `gotyVM`, `playtestsVM`) because
 * mobile rendered the trigger inside the screen. Desktop's Filter Bar is one
 * component shared by all five, so it wants them screen-agnostically and under
 * the names `Filter Bar.dc.html` uses. Same arithmetic, one place.
 *
 * `filterBarVM(s).btnTxt === discoverVM(s).fltBtnTxt` is asserted in
 * `selectors.desktop.test.ts` so the two can never drift.
 */
export function filterBarVM(s: QdStore) {
  // The CURRENT screen's set, and the whole of it: every filter is in force on
  // every screen now, and the picked TAGS count with them since the picker
  // became the last section of this panel. Reading "the current screen" is
  // correct HERE and almost nowhere else — the bar is mounted once and hosted
  // by five screens (`store/filterKey.ts`). The parity assertion in
  // `selectors.desktop.test.ts` holds because both sides run this same sum.
  const n = fltCount(fltOf(s)) + bTagsOf(s).length + xTagsOf(s).length;
  const on = n > 0 || s.fltOpen;
  return {
    ...filtersVM(s),
    btnTxt: n ? '+ FILTERS · ' + n : '+ FILTERS',
    btnBd: on ? 'var(--acc)' : 'var(--gt-border-2)',
    btnClr: on ? 'var(--acc)' : 'var(--gt-text-5)',
    btnBg: on ? 'var(--accSoft)' : 'transparent',
    fltN: n,
  };
}

// ---------------------------------------------------------------------------
// 3. detail — FRIENDS' INTEREST (§3.8)
// ---------------------------------------------------------------------------

export interface DetailVoteRow {
  key: string;
  name: string;
  ini: string;
  hueBg: string;
  hasAv: boolean;
  avBg: string;
  lvlTxt: string;
  lvlClr: string;
  /**
   * Library state — migration 3007, and the V2 roster's third column.
   *
   * `''` while the RPC is in flight, so the column can stay blank rather than
   * claiming NOT OWNED about somebody we have not asked about yet. That is the
   * whole hazard of this field: every other value is a claim.
   */
  libTxt: string;
  libClr: string;
}

/**
 * One row per crew member (prototype line 2418), replacing mobile's grouped
 * avatar clusters (`detailVM.voteGroups`, which stays for the mobile tree).
 * Returns `[]` when the game is not on the list — the panel is only rendered
 * for listed games.
 *
 * D2-06 stays OPEN through the 4-tier pass: the prototype groups these rows
 * into per-call clusters and this tree still lists every member, called or not.
 * Widening the copy from two verdicts to four is not the same change as
 * collapsing the layout, and folding the second into this pass would retire a
 * recorded difference without anyone deciding to (plan decision 5).
 */
/** The roster's library column. Separated so the three states are readable. */
function libState(r: CrewOwnershipRow | undefined): { libTxt: string; libClr: string } {
  if (!r) return { libTxt: '', libClr: 'var(--gt-faint)' };
  if (!r.linked) return { libTxt: 'NO STEAM LINK', libClr: 'var(--gt-faint)' };
  return r.owned
    ? { libTxt: '✓ IN LIBRARY', libClr: 'var(--acc)' }
    : { libTxt: 'NOT OWNED', libClr: 'var(--gt-text-5)' };
}

export function detailVotesRows(s: QdStore, id: GameId): DetailVoteRow[] {
  // OFF THE LIST THE ROSTER STILL STANDS (DESIGN-CHANGELOG 2026-09-03 "The
  // party panel is one roster, not two lists"): the same rows with the
  // ownership line only — `lvlTxt` blank — above the add CTA. Ownership is a
  // fact about the game, not a call.
  const entry = s.list.find((e) => e.id === id);
  const g = s.gById(id);
  if (!g) return [];
  /* Absent = not asked yet; a present empty array = asked and told nothing.
     The first blanks the column, the second is treated the same way — what
     must never happen is either one rendering as NOT OWNED. */
  const owns = g?.appid ? s.crewOwns[g.appid] : undefined;
  const byKey = new Map((owns ?? []).map((r) => [r.userKey, r]));
  return s.users.map<DetailVoteRow>((u) => {
    const c = chipOf(u);
    const v = entry ? entry.votes[u.k] : undefined;
    const L = v && isCall(v) ? LVL[v] : null;
    return {
      key: u.k,
      name: u.name.toUpperCase(),
      ini: c.ini,
      hueBg: c.hueBg,
      hasAv: c.hasAv,
      avBg: c.avBg,
      // RECONCILED against the 2026-08-13 export. `— NO VOTE` was a least-change
      // reconstruction made when "upstream's own builder for this row is in the
      // unreadable tail of the export"; that builder is now readable at
      // `design/Games Tonight.dc.html:3182` and it says `— NO CALL`. Every other
      // surface in the app already says CALL, so the old wording was the last
      // place the retired binary board still spoke.
      //
      // The separator stays `' · '` where the mirror uses a bare space: the
      // mirror is inconsistent with ITSELF here — `myTxt` on the same panel
      // (:3184) uses `' · '` — and `' · '` is the house separator across
      // `voteGroups`, the capsule chips and the What's New cards.
      lvlTxt: !entry ? '' : L ? L.g + ' · ' + L.t : '— NO CALL',
      lvlClr: L ? L.c : 'var(--gt-faint)',
      /* Three states, and the order matters. Not-yet-asked blanks. An unlinked
         member reads NO STEAM LINK — shown, not blanked, per the 2026-08-13
         decision — because blanking would let the roster imply they do not own
         a game nobody has been able to ask them about. Only a LINKED member
         can honestly be reported as NOT OWNED. */
      ...libState(byKey.get(u.k)),
    };
  });
}

// ---------------------------------------------------------------------------
// 4. copy constants that differ from mobile (§8)
// ---------------------------------------------------------------------------

/**
 * The GOTY empty-row hints (prototype line 2335), one per `TIERS` entry. Mobile's
 * live on `G_ROW_META[t].hint` inside `selectors.ts` and read
 * `TAP A TIER BUTTON ABOVE TO PLACE YOUR GOTY` etc. — desktop rows are drop
 * targets, so the copy tells you to drop.
 */
export const GOTY_ROW_HINTS_DESKTOP: Readonly<Record<Tier, string>> = {
  S: 'DROP YOUR GAME OF THE YEAR HERE',
  A: 'DROP THE CONTENDERS',
  B: 'DROP THE HONORABLE MENTIONS',
  C: 'DROP THE SOLID ONES',
  D: 'DROP THE MIDDLING ONES',
  E: 'DROP THE ROUGH ONES',
  F: 'DROP THE LETDOWNS',
  X: 'DROP WHAT DID NOT HOLD UP',
};

/** The GOTY tier-button tooltips (prototype line 2390) — desktop-only. */
export const GOTY_TIER_TIPS: Readonly<Record<Tier, string>> = {
  S: 'S — GAME OF THE YEAR',
  A: 'A — CONTENDER',
  B: 'B — HONORABLE MENTION',
  C: 'C — SOLID',
  D: 'D — MIDDLING',
  E: 'E — ROUGH',
  F: 'F — DID NOT DELIVER',
  X: 'CUT — NOT GOTY',
};

/** Guards the two records above against a future added tier. */
export const GOTY_TIERS: readonly Tier[] = TIERS;

/**
 * `SORT_OPTS` with the one desktop relabel: `SORT: PRICE ↑` → the spelled-out
 * `SORT: PRICE LOW→HIGH` (§8). Every other option is identical.
 */
export const DESKTOP_SORT_OPTS: ReadonlyArray<{ v: SortMode; t: string }> = SORT_OPTS.map((o) =>
  o.v === 'priceAsc' ? { v: o.v, t: 'SORT: PRICE LOW→HIGH' } : o,
);

/**
 * The Discover add-button tooltip used to live here (`addTitle`, prototype
 * line 1441). Since ADD IT (docs/ADD-IT.md) the tip has four states and both
 * shells read it from `TileVM.addTip` — see `addTipOf` in `lib/logic.ts`.
 */

// ---------------------------------------------------------------------------
// 5. tonight — the two additive `title` fields (§7.5)
// ---------------------------------------------------------------------------

export interface DesktopVoteChipTitle {
  key: string;
  title: string;
}

/**
 * `tonightVM` with the two tooltip strings the desktop hover overlay and
 * who's-playing rail need, added as a WRAPPER so `selectors.ts` and its golden
 * fixture stay untouched (see the file header).
 *
 *   capsule chips  `NAME — S · MUST PLAY` per call, `NAME — NO CALL YET` when
 *                  nobody has called; the chip itself only shows a letter.
 *   tfChips        `NAME — SHOWING · CLICK TO REMOVE` when the chip is on;
 *                  `NAME — CLICK TO ADD` when some other filter is on;
 *                  `NAME — CLICK TO SEE JUST THEIR CALLS` when nothing is on.
 *
 * Both are authored UPPERCASE, matching `components/Tooltip.tsx`'s documented
 * contract (see docs/porting/D0.md — the prototype instead lower-cased them and
 * `text-transform`ed them back in CSS).
 */
export function tonightVMDesktop(s: QdStore) {
  const base = tonightVM(s);
  const tFlt = s.tFilter;
  const selKeys = tFlt.length ? tFlt : s.users.map((u) => u.k);
  const nameOf = new Map(s.users.map((u) => [u.k, u.name.toUpperCase()]));

  return {
    ...base,
    sharedRows: base.sharedRows.map((r) => ({
      ...r,
      games: r.games.map((g) => {
        // Read the vote back off the entry rather than inferring it from the
        // chip glyph — `c.t` is the call's letter, which is empty for a member
        // who has not called and says nothing about which call it was until you
        // map it back anyway.
        const votes = s.list.find((e) => e.id === g.id)?.votes ?? {};
        return {
          ...g,
          chips: g.chips.map((c) => {
            const v = votes[c.key];
            const L = v && isCall(v) ? LVL[v] : null;
            return {
              ...c,
              title: (nameOf.get(c.key) ?? '') + ' — ' + (L ? L.g + ' · ' + L.t : 'NO CALL YET'),
            };
          }),
        };
      }),
    })),
    tfChips: base.tfChips.map((u) => {
      const on = tFlt.includes(u.key);
      return {
        ...u,
        title:
          (nameOf.get(u.key) ?? '') +
          (on
            ? ' — SHOWING · CLICK TO REMOVE'
            : tFlt.length
              ? ' — CLICK TO ADD'
              : ' — CLICK TO SEE JUST THEIR CALLS'),
      };
    }),
    /** the keys currently contributing to the aggregation, for D3 convenience */
    selKeys,
  };
}
